/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(
        ':scope > template[shadowrootmode="open"]'
      );

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: "open" });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super("overflowMinimum", { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

export class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position
   */
  constructor(lastVisibleElement) {
    super("reflow", { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ["disabled", "minimum-items"];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "disabled") {
      if (newValue === "true") {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute("defer")) {
      const deferredReflow = () => {
        // Remove attribute first to change layout before calculating the actual size
        this.removeAttribute("defer");
        this.#initialize();
      };
      const { schedule } = this;

      const requestIdleCallback =
        typeof window.requestIdleCallback === "function"
          ? window.requestIdleCallback
          : setTimeout;

      requestIdleCallback(() => schedule(deferredReflow));
    } else if (this.shadowRoot) {
      this.#initialize();
    } else {
      // Not all element children has been parsed yet, try again in the next tick
      // <overflow-list> is a special case as critical.js can execute before DOMContentLoaded
      setTimeout(() => {
        this.#initialize();
      }, 0);
    }
  }

  /**
   * Initialize the element
   */
  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error("Missing shadow root");

    const defaultSlot = shadowRoot.querySelector("slot:not([name])");
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error("Invalid element types in <OverflowList />");
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      "reflow",
      /** @param {ReflowEvent} event */ (event) => {
        this.#reflowItems(event.detail.lastVisibleElement);
      }
    );

    this.#reflowItems();
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === "function"
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute("minimum-items");
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (event) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      // There should only be one entry
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (
        !width ||
        !height ||
        (width === this.#lastDimensions.width &&
          height === this.#lastDimensions.height)
      ) {
        // Skip reflow if dimensions are 0 or the same as the last reflow
        return;
      }

      this.#lastDimensions = {
        width: Math.round(width),
        height: Math.round(height),
      };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty("height");
    this.style.setProperty("--overflow-count", "0");
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute("minimum-reached", "");
      } else {
        this.removeAttribute("minimum-reached");
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty("width", "0");
    placeholder.style.setProperty("display", "none");
    this.setAttribute("disabled", "true");
  }

  /**
   * Reflow items based on available space within the list.
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } =
      this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    // Avoid layout shifts while reflowing the list
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};

    if (height) list.style.height = `${height}px`;
    list.style.setProperty("overflow", "hidden");

    // Move all elements to the default slot so we can check which ones overflow
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    // Make sure the "More" item and placeholder are hidden
    moreSlot.hidden = true;
    placeholder.hidden = true;

    // First, check if all the items fit
    const rootRect = list.getBoundingClientRect();

    // Store the initial dimensions for comparison later
    this.#lastDimensions = {
      width: Math.round(rootRect.width),
      height: Math.round(rootRect.height),
    };

    const getVisibleElements = () =>
      elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
    let visibleElements = getVisibleElements();

    // If not all items fit or we have a lastVisibleElement, let's calculate with "More" button
    if (visibleElements.length !== elements.length || lastVisibleElement) {
      // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row
      moreSlot.style.setProperty("order", "-1");
      moreSlot.hidden = false;
      moreSlot.style.setProperty("height", `${height}px`);

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty("order", "-1");
      }

      // Recalculate the visible elements
      visibleElements = getVisibleElements();

      // Reset the order
      moreSlot.style.removeProperty("order");
      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty("order");
      }

      // If we have a lastVisibleElement, ensure it's in the last visible position
      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          // Remove lastVisibleElement from its current position
          visibleElements.splice(visibleIndex, 1);
          // Add it to the end of visible elements
          visibleElements.push(lastVisibleElement);
        }
      }

      moreSlot.style.setProperty("height", "auto");
    }

    const overflowingElements = elements.filter(
      (element) => !visibleElements.includes(element)
    );
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement
      ? firstOverflowingElement.clientWidth
      : 0;

    // Move the elements to the correct slot
    for (const element of elements) {
      element.slot = overflowingElements.includes(element)
        ? overflowSlot.name
        : defaultSlot.name;
    }

    // If there are overflowing elements
    // Show more button and placeholder if needed
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty(
      "counter-reset",
      `overflow-count ${overflowingElements.length}`
    );
    this.style.setProperty("--overflow-count", `${overflowingElements.length}`);
    list.style.removeProperty("overflow");

    // Check if the minimum-reached state should be updated
    hasOverflow && this.#updateMinimumReached(visibleElements);

    // Observe the list for changes in size
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get("overflow-list")) {
  customElements.define("overflow-list", OverflowList);
}

// Function to calculate total height of header group children
export function calculateHeaderGroupHeight(
  header = document.querySelector("#header-component"),
  headerGroup = document.querySelector("#header-group")
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }

  // If the header is transparent and has a sibling section, add the height of the header to the total height
  if (
    header instanceof HTMLElement &&
    header.hasAttribute("transparent") &&
    header.parentElement?.nextElementSibling
  ) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

/**
 * Initialize and maintain header height CSS variables.
 * This is critical for preventing layout shifts during page load.
 * There is a `ResizeObserver` and `MutationObserver` that kicks in post hydration in header.js
 * Note: header-group uses display: contents, so we must observe all children.
 */
function updateHeaderHeights() {
  const header = document.querySelector("header-component");

  // Early exit if no header - nothing to do
  if (!(header instanceof HTMLElement)) return;

  // Calculate initial height(s
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  document.body.style.setProperty("--header-height", `${headerHeight}px`);
  document.body.style.setProperty(
    "--header-group-height",
    `${headerGroupHeight}px`
  );
}

/**
 * Updates CSS custom properties for transparent header offset calculation
 * Avoids expensive :has() selectors
 */
function updateTransparentHeaderOffset() {
  const header = document.querySelector("#header-component");
  const headerGroup = document.querySelector("#header-group");
  const hasHeaderSection = headerGroup?.querySelector(".header-section");
  if (!hasHeaderSection || !header?.hasAttribute("transparent")) {
    document.body.style.setProperty("--transparent-header-offset-boolean", "0");
    return;
  }

  const hasImmediateSection =
    hasHeaderSection.nextElementSibling?.classList.contains("shopify-section");

  const shouldApplyOffset = !hasImmediateSection ? "1" : "0";
  document.body.style.setProperty(
    "--transparent-header-offset-boolean",
    shouldApplyOffset
  );
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

// Run both functions on page load
updateAllHeaderCustomProperties();

// Because Shopify's Forms app uses shadow DOM, we need to inject our styles into the shadow root
customElements.whenDefined("shopify-forms-embed").then(() => {
  // Function to style a form element
  function styleFormElement(formElement) {
    if (formElement.shadowRoot) {
      const observer = new MutationObserver(() => {
        if (formElement.shadowRoot.querySelector("form")) {
          const style = document.createElement("style");
          style.textContent = `
            section[role="dialog"] {
              margin: 0 !important;
            }

            /* Form header - heading */
            [class*='_formHeader'] {
              margin-bottom: 24px !important;
            }

            [class*='_textHeading'] {
              font-family: var(--font-heading--family) !important;
              font-size: var(--font-h3--size, 2.5rem) !important;
              font-weight: var(--font-heading--weight, 400) !important;
              line-height: var(--font-heading--line-height, 1.1) !important;
              color: var(--color-foreground-heading) !important;
              margin-bottom: 12px !important;
            }

            [class*='_textHeading'] em,
            [class*='_textHeading'] i {
              font-style: italic !important;
              font-family: var(--font-heading-italic--family, var(--font-heading--family)) !important;
            }

            /* Form header - body text */
            [class*='_textBody'] {
              font-family: var(--font-body--family) !important;
              font-size: var(--font-paragraph--size, 1.125rem) !important;
              line-height: var(--font-paragraph--line-height, 1.5) !important;
              color: var(--color-foreground) !important;
            }

            form {
              padding: 0 !important;
            }

            form {
              gap: 6px !important;
              padding: 0 !important;
            }

            input,
            textarea {
              padding: 12px 14px !important;
              outline: 1px solid #D1D0D0 !important;
              box-shadow: none !important;
              line-height: 24px !important;
            }
            
            input:focus-visible,
            textarea:focus-visible {
              outline: 2px solid #181412 !important;
            }
            
            div[class*=' _withError'] > input,
            textarea[class*='_invalid'] {
              outline: 1px solid var(--form-error-color) !important;
            }

            div[class*=' _withError'] > input:focus-visible,
            textarea[class*='_invalid']:focus-visible {
              outline: 2px solid var(--form-error-color) !important;
            }

            output.[class^='_formError'] {
              font-size: 14px !important;
            }

            textarea:before {
              display: none !important;
            }

            [class^='_inline'],
            [class^='_formContainer'],
            [class^='_gridItem'] {
              overflow: visible !important;
            }

            [class^='_formFieldContainer']::before {
              display: none !important;
            }

            // * + [class^='_formFieldContainer'] {
            //   margin-top: 10px !important;
            // }

            label {
              position: static !important; 
              order: -1;
              padding-bottom: 6px;
              color: var(--body-text-color) !important;
              transform: none !important;
              font-size: 14px !important;
              font-weight: 600 !important;
              line-height: 20px !important;
            }

            button {
              --button-color: var(--color-primary-button-text);
              --button-background-color: var(--color-primary-button-background);
              --button-border-color: var(--color-primary-button-border);
              --button-border-width: var(--style-border-width-primary);

              margin-top: 10px !important;
              
              color: var(--button-color) !important;
              background-color: var(--button-background-color) !important;
              font-family: var(--font-paragraph--family) !important;
              font-style: var(--font-paragraph--style) !important;
              font-size: var(--button-large-font-size, --font-paragraph--size) !important;
              line-height: var(--button-large-line-height, --font-paragraph--line-height) !important;
              font-weight: var(--button-font-weight, --font-paragraph--weight) !important;
              transition: all var(--animation-speed) var(--animation-easing) !important;
              cursor: pointer;

              padding-block: var(--button-large-padding-block) !important;
              padding-inline: var(--button-large-padding-inline) !important;

              border: var(--button-border-width) solid var(--button-border-color, var(--button-background)) !important;
              /* Focus outline using box-shadow to avoid shifting layout */
              --button-ring-offset: 0;
              --button-ring-width: 2px;
              --button-ring-color: currentColor;
              box-shadow: 0 0 0 calc(var(--button-ring-width) + var(--button-ring-offset)) var(--button-ring-color) !important;
              --button-outline-offset: 2px;
              --button-outline-width: 2px;
              --button-outline-color: transparent;
              outline: var(--button-outline-width) solid var(--button-outline-color) !important;
              outline-offset: var(--button-outline-offset) !important;

              filter: none !important;
            }

            button:hover {
              --button-color: var(--color-primary-button-hover-text);
              --button-background-color: var(--color-primary-button-hover-background);
              --button-border-color: var(--color-primary-button-hover-border);
            }

            button:focus {
              --button-ring-color: var(--color-primary-button-focus-ring);
              --button-outline-color: var(--color-primary-button-focus-outline);
            }

            /* Phone input - country selector and input on same line */
            [class*='_formPhoneInputContainer'] {
              display: flex !important;
              flex-direction: row !important;
              flex-wrap: wrap !important;
              align-items: flex-end !important;
              gap: 8px !important;
              position: relative !important;
              // margin-top: 10px !important;
            }

            [class*='_formPhoneInputContainer'] > .phone-country-selector {
              flex-shrink: 0 !important;
            }

            [class*='_formPhoneInputContainer'] > [class^='_formFieldContainer'] {
              flex: 1 !important;
              margin-top: 0 !important;
            }
            
            [class*='_formPhoneInputContainer'] output {
              flex-basis: 100% !important;
              width: 100% !important;
              margin-top: 6px !important;
            }

            /* Country selector button - reset to not look like submit button */
            [class*='_formPhoneInputContainer'] button[type="button"] {
              all: revert !important;
              display: flex !important;
              align-items: center !important;
              gap: 8px !important;
              height: 50px !important;
              padding: 12px 14px !important;
              border: none !important;
              outline: 1px solid #D1D0D0 !important;
              border-radius: 5px !important;
              background: white !important;
              cursor: pointer !important;
              box-sizing: border-box !important;
            }

            [class*='_formPhoneInputContainer'] button[type="button"]:focus-visible {
              outline: 2px solid #181412 !important;
            }

            /* Phone label - full width above both inputs */
            [class*='_formPhoneInputContainer'] label {
              position: static !important;
              width: 100% !important;
              order: -1 !important;
              padding-bottom: 0 !important;
              margin-bottom: -2px !important;
              color: var(--body-text-color) !important;
              transform: none !important;
              font-size: 14px !important;
              font-weight: 600 !important;
              line-height: 20px !important;
            }

            /* Phone input field container */
            [class*='_formPhoneInputContainer'] > [class^='_formFieldContainer'] {
              display: contents !important;
            }

            /* Phone input field - match height and fill remaining width */
            [class*='_formPhoneInputContainer'] input {
              // height: 50px !important;
              flex: 1 !important;
              min-width: 0 !important;
              box-sizing: border-box !important;
            }

            /* File input - reset button styles */
            [class*='_formFileInputField'] {
              all: revert !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              width: 100% !important;
              min-height: 50px !important;
              padding: 12px 14px !important;
              border: none !important;
              outline: 1px dashed #D1D0D0 !important;
              border-radius: 5px !important;
              background: white !important;
              cursor: pointer !important;
              box-sizing: border-box !important;
            }

            [class*='_formFileInputField']:hover {
              outline: 1px dashed #181412 !important;
            }

            [class*='_formFileInputField']:focus-visible {
              outline: 2px solid #181412 !important;
            }

            /* Hide the inner button, just show text */
            [class*='_formFileInputButton'] {
              all: revert !important;
              background: none !important;
              border: none !important;
              padding: 0 !important;
              margin: 0 !important;
              font-size: 14px !important;
              color: var(--body-text-color, #181412) !important;
              cursor: pointer !important;
            }

            /* File input container - add top margin like other fields */
            [class*='_formFileInput'] {
              margin-top: 10px !important;
            }

            /* File input label (CV) - match other labels */
            [class*='_formFileInput'] > p {
              font-size: 14px !important;
              font-weight: 600 !important;
              line-height: 20px !important;
              color: var(--body-text-color) !important;
              margin: 0 0 6px 0 !important;
              padding: 0 !important;
            }

            /* Radio fieldset - add top margin like other fields */
            [class*='_radioFieldSet'],
            fieldset[class*='_radioFieldSet'] {
              margin-top: 10px !important;
              border: none !important;
              padding: 0 !important;
            }

            /* Radio fieldset legend - match other labels */
            [class*='_radioFieldSet'] > legend,
            legend[class*='_radioLegend'] {
              font-size: 14px !important;
              font-weight: 600 !important;
              line-height: 20px !important;
              color: var(--body-text-color) !important;
              padding: 0 0 6px 0 !important;
              margin: 0 !important;
            }

            /* Success banner container */
            [class*='_formBannerContainer'] {
              padding: 0 !important;
              background: transparent !important;
              border-radius: 0 !important;
              margin-top: 0 !important;
            }

            /* Success banner heading - match site heading style */
            [class*='_formBannerHeading'] {
              font-family: var(--font-heading--family) !important;
              font-size: var(--font-h3--size, 2.5rem) !important;
              font-weight: var(--font-heading--weight, 400) !important;
              line-height: var(--font-heading--line-height, 1.1) !important;
              margin-bottom: 12px !important;
              color: var(--color-foreground-heading) !important;
            }

            [class*='_formBannerHeading'] em {
              margin-left: 0.15em !important;
            }

            /* Success banner body text */
            [class*='_formBannerContainer'] [class*='_textBody'] {
              font-family: var(--font-body--family) !important;
              font-size: var(--font-paragraph--size, 1.125rem) !important;
              line-height: var(--font-paragraph--line-height, 1.5) !important;
              color: var(--color-foreground) !important;
              margin-top: 10px !important;
            }
            
            [class*='_selectContainer'] button[type="button"] {
              all: revert !important;
              display: flex !important;
              align-items: center !important;
              gap: 8px !important;
              height: 50px !important;
              width: 100% !important;
              padding: 12px 14px !important;
              border: none !important;
              outline: 1px solid #D1D0D0 !important;
              border-radius: var(--form-field-border-radius, 16px) !important;
              background: white !important;
              cursor: pointer !important;
              box-sizing: border-box !important;
            }

            button[type="button"][class*='_copyToClipboardButton'] {
              all: revert !important;
              display: flex !important;
              align-items: center !important;
              gap: 8px !important;
              height: 50px !important;
              width: 100% !important;
              padding: 12px 14px !important;
              border: none !important;
              outline: 1px solid #D1D0D0 !important;
              border-radius: var(--form-field-border-radius, 16px) !important;
              background: white !important;
              cursor: pointer !important;
              box-sizing: border-box !important;
              justify-content: space-between !important;
            }

            [class*='_formContainer'] {
              min-width: 330px !important;
              max-width: 100% !important;
              width: 100% !important;
            }

            [class*='_formFieldContainer'] label {
              font-weight: unset !important;
              font-size: var(--font-paragraph--size, 1.125rem) !important;
              font-family: var(--font-body--family) !important;
            }

            [class*='_selectContainer'] [class*='label'] {
              color: var(--body-text-color) !important;
            }

            [class*='_consentCheckboxContainer'] [class*='_textBody'] {
              color: var(--body-text-color) !important;
            }

            [class*='_consentCheckboxContainer'] input[type='checkbox'] {
              outline: none !important;
            }
            
            section[role="dialog"] [class*='_formHeader'] {
              margin-bottom: 0px !important;
            }
            
            [class*='_formFieldContainer'] label {
              color: var(--shopify-form-label-color) !important;
              font-size: var(--shopify-form-label-font-size, 1rem) !important;
              line-height: var(--shopify-form-label-line-height, 1.125rem) !important;
            }

            [class*='_formDisclaimer'] {
              color: var(--shopify-form-label-color) !important;
            }

            [class*='_formHeader'] h2 {
              font-size: 1rem !important;
              font-weight: var(--font-body--weight) !important;
            }

            input,
            textarea {
              padding: var(--shopify-input-padding-top, 12px) 14px !important;
            }

            [class*='_selectContainer'] button[type="button"] {
              height: var(--shopify-select-height, unset) !important;
            }

            [class*='_verticalLayout'] {
              gap: var(--shopify-vertical-layout-gap, 10px) !important;
            }

            [class*='_formHeader'] h2 {
              font-size: var(--shopify-text-heading, 1rem) !important;
            }

            [class*='_textBody'] {
              font-size: var(--shopify-text-body, 1rem) !important;
            }



          `;
          formElement.shadowRoot.appendChild(style);

          // Listen for form submissions on rituals and newsletter pages
          const isTrackingPage = window.location.pathname === "/pages/rituals" || window.location.pathname === "/pages/newsletter";
          const form = formElement.shadowRoot.querySelector("form");
          if (form && isTrackingPage) {
            form.addEventListener("submit", () => {
              const firstNameInput = form.querySelector('input#first_name, input[id*="first_name"], input[name*="first"]');
              const emailInput = form.querySelector('input#email, input[id*="email"], input[type="email"], input[name*="email"]');

              const firstName = firstNameInput?.value?.trim() || "";
              const email = emailInput?.value?.trim() || "";

              if (email) {
                window.analytics.track("Email Subscribed", {
                  first_name: firstName,
                  email: email,
                  campaign_name: "The Ritual"
                });
              }
            });
          }

          // Modify heading text to add line break and italics for "inner circle"
          const heading = formElement.shadowRoot.querySelector('[class*="_textHeading"]');
          if (heading) {
            const text = heading.textContent;
            if (text && text.includes('inner circle')) {
              heading.innerHTML = text
                .replace('know. Join', 'know.<br>Join')
                .replace('inner circle', '<em>inner circle</em>');
            }
          }

          // Watch for success banner and make "waitlist" italic (studio waitlist form)
          const successObserver = new MutationObserver(() => {
            const bannerHeading = formElement.shadowRoot.querySelector('[class*="_formBannerHeading"]');
            if (bannerHeading && bannerHeading.textContent.includes('waitlist')) {
              bannerHeading.innerHTML = bannerHeading.textContent.replace('the waitlist', 'the <em>waitlist</em>');
              successObserver.disconnect();
            }
          });

          successObserver.observe(formElement.shadowRoot, {
            childList: true,
            subtree: true,
          });

          // Studio trainer form - modify heading to make "your approach" italic
          const trainerHeading = formElement.shadowRoot.querySelector('[class*="_textHeading"]');
          if (trainerHeading) {
            const trainerText = trainerHeading.textContent;
            if (trainerText && trainerText.includes('your approach')) {
              trainerHeading.innerHTML = trainerText.replace('your approach', '<em>your approach</em>');
            }
          }

          // Watch for success banner on trainer form
          const trainerSuccessObserver = new MutationObserver(() => {
            const bannerHeading = formElement.shadowRoot.querySelector('[class*="_formBannerHeading"]');
            if (bannerHeading && bannerHeading.textContent.includes('Application')) {
              const text = bannerHeading.textContent;
              if (text.includes('submitted')) {
                bannerHeading.innerHTML = text.replace('submitted', '<em>submitted</em>');
              }
              trainerSuccessObserver.disconnect();
            }
          });

          trainerSuccessObserver.observe(formElement.shadowRoot, {
            childList: true,
            subtree: true,
          });

          observer.disconnect();
        }
      });

      observer.observe(formElement.shadowRoot, {
        childList: true,
        subtree: true,
      });
    }
  }

  // Check for existing forms
  const existingForms = document.querySelectorAll("shopify-forms-embed");
  console.log("Found existing forms:", existingForms.length);
  existingForms.forEach(styleFormElement);

  // Watch for new forms being added to the page
  const documentObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node.tagName === "SHOPIFY-FORMS-EMBED") {
          console.log("New form element detected");
          styleFormElement(node);
        }
      });
    });
  });

  documentObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
});
